import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import HomePage from "./pages/HomePage";
import CookieConsent from "./components/CookieConsent";
import { bootConsentOnLoad } from "./utils/consentManager";
import { trackView, startHeartbeat, initClickTracking } from "./utils/trafficTracker";

const ContactPage = lazy(() => import("./pages/ContactPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const LocalPage = lazy(() => import("./pages/LocalPage"));
const DecisionPage = lazy(() => import("./pages/DecisionPage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage"));
const AdminStatsPage = lazy(() => import("./pages/AdminStatsPage"));
const AdminTrafficPage = lazy(() => import("./pages/AdminTrafficPage"));
const AdminAgiPage = lazy(() => import("./pages/AdminAgiPage"));

import { decisionPageSlugs } from "./data/decisionPagesData";
import { brandPageSlugs } from "./data/brandPagesData";

bootConsentOnLoad();

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const TrafficTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => { trackView(pathname); }, [pathname]);
  useEffect(() => { startHeartbeat(); initClickTracking(); }, []);
  return null;
};

const WebmailRedirect = () => {
  useEffect(() => { window.location.replace('https://giowm1080.siteground.biz/webmail'); }, []);
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="App" data-testid="consultauto-app">
          <BrowserRouter>
            <ScrollToTop />
            <TrafficTracker />
            <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/rendez-vous" element={<BookingPage />} />
              <Route path="/merci" element={<ThankYouPage />} />
              <Route path="/webmail" element={<WebmailRedirect />} />
              <Route path="/prendre-rendez-vous" element={<Navigate to="/rendez-vous" replace />} />
              <Route path="/rendez-vous-2" element={<Navigate to="/rendez-vous" replace />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/politique-de-confidentialite" element={<PrivacyPage />} />
              <Route path="/confidentialite" element={<Navigate to="/politique-de-confidentialite" replace />} />
              <Route path="/privacy" element={<Navigate to="/politique-de-confidentialite" replace />} />
              <Route path="/conditions" element={<TermsPage />} />
              <Route path="/terms" element={<Navigate to="/conditions" replace />} />
              {decisionPageSlugs.map(slug => (
                <Route key={slug} path={`/${slug}`} element={<DecisionPage />} />
              ))}
              {brandPageSlugs.map(slug => (
                <Route key={slug} path={`/${slug}`} element={<BrandPage />} />
              ))}
              <Route path="/admin/stats" element={<AdminStatsPage />} />
              <Route path="/admin/trafic" element={<AdminTrafficPage />} />
              <Route path="/admin/agi" element={<AdminAgiPage />} />
              <Route path="/:slug" element={<LocalPage />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          <CookieConsent />
          <Toaster position="top-right" />
        </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
